import type { KeyboardEvent, ReactNode } from 'react';
import type { IncomingImageInput } from './use-incoming-image-inputs';

const MENTION_RE = /@\[[^\]]+\]/g;

export function hasMention(text: string | undefined | null): boolean {
  return typeof text === 'string' && text.includes('@[');
}

export function findMentionRanges(text: string): { start: number; end: number }[] {
  const ranges: { start: number; end: number }[] = [];
  for (const m of text.matchAll(MENTION_RE)) {
    ranges.push({ start: m.index!, end: m.index! + m[0].length });
  }
  return ranges;
}

/**
 * 检测光标前是否存在未完成的 `@` 触发（用于弹出提及菜单）。
 * 返回 `@` 的下标；不满足条件（已是 @[ 区块 / 中间有空白）时返回 null。
 */
export function detectMentionAnchor(text: string, cursorPos: number): number | null {
  const before = text.slice(0, cursorPos);
  const atIdx = before.lastIndexOf('@');
  if (atIdx >= 0 && before[atIdx + 1] !== '[' && !/\s/.test(before.slice(atIdx + 1))) return atIdx;
  return null;
}

/**
 * 点击/按键后把落在 @[..] 区块内部的光标吸附到区块边界。
 * select=true（点击）时整块选中，false（键盘移动）时吸附到较近一侧。
 */
export function resolveMentionCaret(el: HTMLTextAreaElement, select: boolean): void {
  const text = el.value;
  if (!hasMention(text)) return;
  const s = el.selectionStart;
  const e = el.selectionEnd;
  if (s !== e) return;
  for (const r of findMentionRanges(text)) {
    if (s > r.start && s < r.end) {
      if (select) {
        el.selectionStart = r.start;
        el.selectionEnd = text[r.end] === ' ' ? r.end + 1 : r.end;
      } else {
        const pos = (s - r.start) <= (r.end - s) ? r.start : r.end;
        el.selectionStart = pos;
        el.selectionEnd = pos;
      }
      return;
    }
  }
}

/**
 * 让 @[..] 区块表现为原子单元的键盘处理：
 * - ArrowLeft/Right：整块跳过；
 * - Backspace/Delete：整块删除（连同区块后紧跟的一个空格）。
 * 返回 true 表示事件已处理（已 preventDefault）；applyValue 负责把新值写回受控状态。
 */
export function handleMentionAtomicKeys(
  e: KeyboardEvent<HTMLTextAreaElement>,
  applyValue: (next: string) => void,
): boolean {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Backspace' && e.key !== 'Delete') return false;
  const el = e.currentTarget;
  const text = el.value;
  if (!hasMention(text)) return false;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  if (start !== end) return false;
  const ranges = findMentionRanges(text);

  if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !e.shiftKey) {
    for (const r of ranges) {
      if (e.key === 'ArrowLeft' && start > r.start && start <= r.end) {
        e.preventDefault();
        el.selectionStart = r.start;
        el.selectionEnd = r.start;
        return true;
      }
      if (e.key === 'ArrowRight' && start >= r.start && start < r.end) {
        e.preventDefault();
        el.selectionStart = r.end;
        el.selectionEnd = r.end;
        return true;
      }
    }
    return false;
  }

  const deleteRange = (r: { start: number; end: number }) => {
    e.preventDefault();
    const delEnd = text[r.end] === ' ' ? r.end + 1 : r.end;
    applyValue(text.slice(0, r.start) + text.slice(delEnd));
    setTimeout(() => {
      el.selectionStart = r.start;
      el.selectionEnd = r.start;
    }, 0);
  };

  if (e.key === 'Backspace' && start > 0) {
    for (const r of ranges) {
      if (start > r.start && start <= r.end) { deleteRange(r); return true; }
      if (start === r.end + 1 && text[r.end] === ' ') { deleteRange(r); return true; }
    }
  }
  if (e.key === 'Delete' && start < text.length) {
    for (const r of ranges) {
      if (start >= r.start && start < r.end) { deleteRange(r); return true; }
    }
  }
  return false;
}

export function renderMentionMirror(text: string, fontSize?: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(MENTION_RE)) {
    const start = m.index!;
    if (start > last) parts.push(<span key={`t${last}`}>{text.slice(last, start)}</span>);
    parts.push(
      <span key={`m${start}`} className={`rounded bg-primary/20 text-primary ring-1 ring-inset ring-primary/30 [box-decoration-break:clone] py-0.5 ${fontSize ?? 'text-sm'}`}>
        {m[0]}
      </span>
    );
    last = start + m[0].length;
  }
  if (last < text.length) parts.push(<span key={`t${last}`}>{text.slice(last)}</span>);
  // 末尾追加零宽字符：textarea 会为结尾的换行符渲染出一个空行，而 pre-wrap 的 div 不会，
  // 导致两层 scrollHeight 不一致 → 滚动时 mirror 被钳制、文本相对光标整体偏移（“光标错乱”）。
  parts.push(<span key="zw-tail">{'\u200b'}</span>);
  return parts;
}

type MediaKey = 'image' | 'video' | 'audio';

/** token 中序号与名字之间的分隔符：@[图像1·海螺] */
export const MENTION_NAME_SEP = '·';
const MENTION_NAME_MAX_CHARS = 16;

/**
 * 计算 mention token 的名字后缀（含分隔符），如 `·海螺`；上游节点未被用户命名时返回空串。
 * 名字来源与缩略图标签一致：素材备注 assetNote > 节点标题 name；
 * 等于默认节点标题（如「图像节点」）视为未命名。
 */
export function buildMentionNameSuffix(
  img: Pick<IncomingImageInput, 'kind' | 'subIndex' | 'subTotal'>,
  sourceNode: { data?: unknown } | undefined | null,
  t: (key: string) => string,
): string {
  if (!sourceNode || img.kind === 'subjectGroup') return '';
  const data = (sourceNode.data ?? {}) as Record<string, unknown>;
  const defaults = new Set(
    ['Image Node', 'Video Node', 'Audio Node', 'Upload Node', 'File Node'].map((k) => t(k)),
  );
  const pick = (v: unknown) => {
    const s = typeof v === 'string' ? v.trim() : '';
    return s && !defaults.has(s) ? s : '';
  };
  let name = pick(data.assetNote) || pick(data.name);
  // 去掉会破坏 token 结构的字符（方括号 / @ / 换行），并压缩空白
  name = name.replace(/[\[\]@\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!name) return '';
  const chars = Array.from(name);
  if (chars.length > MENTION_NAME_MAX_CHARS) name = `${chars.slice(0, MENTION_NAME_MAX_CHARS).join('')}…`;
  if (typeof img.subIndex === 'number' && (img.subTotal ?? 0) > 1) name = `${name} ${img.subIndex + 1}/${img.subTotal}`;
  return `${MENTION_NAME_SEP}${name}`;
}

export type MentionLabels = { image: string; video: string; audio: string };

const MEDIA_KEYS = ['image', 'video', 'audio'] as const;

const escapeRegex = (s: string) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const emptyIdxMap = (): Record<MediaKey, Map<number, number | null>> => ({
  image: new Map(), video: new Map(), audio: new Map(),
});

/**
 * 通用：按「旧序号 → 新序号 / null(删除)」映射重写 prompt 中 @[Label{N}] 引用。
 * 使用占位符二段替换避免 1↔2 这种链式 / 互换冲突。被映射为 null 的会连同后续的一个空格一起删除。
 */
export function rewriteMentionsByIdxMap(
  text: string,
  idxMap: Record<MediaKey, Map<number, number | null>>,
  labels: MentionLabels,
): string {
  if (!text) return text;

  let hadDeletion = false;
  let hadChange = false;
  let out = text;

  for (const mt of MEDIA_KEYS) {
    const label = labels[mt];
    if (!label || idxMap[mt].size === 0) continue;
    // 可选的名字后缀（@[图像1·海螺]）随 token 一起保留，名字跟随的是同一素材，序号变化不影响
    const re = new RegExp(`@\\[${escapeRegex(label)}(\\d+)((?:${MENTION_NAME_SEP}[^\\]\\n]*)?)\\]( ?)`, 'g');
    out = out.replace(re, (full, n: string, nameSuffix: string, sp: string) => {
      const oldIdx = parseInt(n, 10);
      const next = idxMap[mt].get(oldIdx);
      if (next === undefined) return full;
      hadChange = true;
      if (next === null) { hadDeletion = true; return ''; }
      // 用占位符承载新序号，二段替换时再展开成本地化 label
      return `@[\u0001${mt}\u0001${next}\u0001${nameSuffix}]${sp}`;
    });
  }

  if (!hadChange) return text;

  for (const mt of MEDIA_KEYS) {
    const re = new RegExp(`@\\[\\u0001${mt}\\u0001(\\d+)\\u0001`, 'g');
    out = out.replace(re, (_full, n: string) => `@[${labels[mt]}${n}`);
  }
  // 仅在产生删除时才合并多余空格，避免吞掉用户原本就有的双空格
  return hadDeletion ? out.replace(/[ \t]{2,}/g, ' ') : out;
}

/** 把 mention 列表归并成 `mediaType -> rid -> idx` 的快表。 */
function buildRidIdxByType(
  inputs: IncomingImageInput[],
): Record<MediaKey, Map<string, number>> {
  const result: Record<MediaKey, Map<string, number>> = {
    image: new Map(), video: new Map(), audio: new Map(),
  };
  for (const it of inputs) {
    if (it.kind === 'subjectGroup') continue;
    if (!it.idx) continue;
    const mt = (it.mediaType ?? 'image') as MediaKey;
    result[mt].set(it.rid, it.idx);
  }
  return result;
}

/**
 * 删除/断开后重写 mentions：被删项 → null，剩余项按原相对顺序重新 1..N。
 */
export function rewriteMentionsAfterRemoval(
  text: string,
  mentionInputs: IncomingImageInput[],
  shouldRemove: (it: IncomingImageInput) => boolean,
  labels: MentionLabels,
): string {
  if (!text) return text;
  const byType: Record<MediaKey, { kept: number[]; removed: Set<number> }> = {
    image: { kept: [], removed: new Set() },
    video: { kept: [], removed: new Set() },
    audio: { kept: [], removed: new Set() },
  };
  for (const it of mentionInputs) {
    if (it.kind === 'subjectGroup' || !it.idx) continue;
    const mt = (it.mediaType ?? 'image') as MediaKey;
    if (shouldRemove(it)) byType[mt].removed.add(it.idx);
    else byType[mt].kept.push(it.idx);
  }
  const idxMap = emptyIdxMap();
  for (const mt of MEDIA_KEYS) {
    for (const r of byType[mt].removed) idxMap[mt].set(r, null);
    byType[mt].kept
      .sort((a, b) => a - b)
      .forEach((oldIdx, i) => {
        const newIdx = i + 1;
        if (newIdx !== oldIdx) idxMap[mt].set(oldIdx, newIdx);
      });
  }
  return rewriteMentionsByIdxMap(text, idxMap, labels);
}

/**
 * 重排序后重写 mentions：根据新旧 mention 列表（同一 rid 的 idx 可能已变）建立映射。
 */
export function rewriteMentionsAfterReorder(
  text: string,
  oldInputs: IncomingImageInput[],
  newInputs: IncomingImageInput[],
  labels: MentionLabels,
): string {
  if (!text) return text;
  const oldByType = buildRidIdxByType(oldInputs);
  const newByType = buildRidIdxByType(newInputs);
  const idxMap = emptyIdxMap();
  for (const mt of MEDIA_KEYS) {
    for (const [rid, oldIdx] of oldByType[mt].entries()) {
      const next = newByType[mt].get(rid);
      if (next != null && next !== oldIdx) idxMap[mt].set(oldIdx, next);
    }
  }
  return rewriteMentionsByIdxMap(text, idxMap, labels);
}